import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Truck, ShieldCheck, MapPin, Phone, Mail } from "lucide-react";
import { api, fmtPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { MaskedLines, FadeUp } from "../components/Reveal";
import { scrollToId } from "../lib/scroll";
import { toast } from "sonner";

const LIFESTYLE = "https://images.unsplash.com/photo-1752661497402-5b3b6743d66a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxnb2xmZXIlMjBzdW5zZXR8ZW58MHx8fHwxNzg2NDk0MTU0fDA&ixlib=rb-4.1.0&q=85";
const HERO_IMG = "https://images.unsplash.com/photo-1599601438065-af12a89792e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxnb2xmJTIwY2FydCUyMGJsYWNrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODY0OTQxNTR8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  return (
    <div>
      <Hero />
      <Fleet products={products} />
      <Manifesto />
      <Testimonials />
      <Shipping />
      <Contact />
      <Footer />
    </div>
  );
}

/* ---------------- HERO ---------------- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  return (
    <section ref={ref} className="relative h-screen min-h-[720px] overflow-hidden flex items-center" data-testid="hero">
      {/* spotlight */}
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(circle at 70% 40%, rgba(212,175,55,0.12), transparent 55%)" }}
      />
      <motion.div style={{ y }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full">
        <motion.p
          className="font-label uppercase tracking-[0.4em] text-xs text-gold mb-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
        >
          Sold & Delivered Worldwide
        </motion.p>
        <motion.img
          src="/ggc-logo.png"
          alt="Global Golf Carts LLC"
          className="mt-6 w-[240px] sm:w-[300px] lg:w-[360px] h-auto drop-shadow-[0_0_40px_rgba(212,175,55,0.15)]"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
        />
        <FadeUp delay={0.9} className="mt-8 max-w-md">
          <p className="font-body text-[var(--ggc-muted)] text-base leading-relaxed">
            A curated fleet of premium electric golf carts — engineered, appointed and delivered
            to your door in all fifty states.
          </p>
        </FadeUp>
        <FadeUp delay={1.05} className="mt-10 flex flex-wrap gap-4">
          <button
            onClick={() => scrollToId("fleet")}
            className="group bg-gold text-black font-label uppercase tracking-[0.2em] text-sm px-8 py-4 rounded-sm flex items-center gap-2 hover:bg-[var(--ggc-gold-hover)] transition-colors"
            data-testid="hero-explore-btn"
          >
            Explore The Fleet
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
          <button
            onClick={() => scrollToId("contact")}
            className="border font-label uppercase tracking-[0.2em] text-sm px-8 py-4 rounded-sm hover:border-[var(--ggc-gold)] hover:text-gold transition-colors"
            style={{ borderColor: "var(--ggc-border)" }}
          >
            Talk To Us
          </button>
        </FadeUp>
      </motion.div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[var(--ggc-muted)] font-label uppercase tracking-[0.3em] text-[10px]">
        Scroll
      </div>
    </section>
  );
};

/* ---------------- FLEET ---------------- */
const Fleet = ({ products }) => {
  const { addItem } = useCart();
  return (
    <section id="fleet" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="fleet-section">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
        <div>
          <p className="font-label uppercase tracking-[0.4em] text-xs text-gold mb-4">The Collection</p>
          <MaskedLines
            as="h2"
            className="font-serif-ed text-5xl md:text-7xl leading-[0.95]"
            lines={["Choose Your", "Machine"]}
          />
        </div>
        <p className="font-body text-[var(--ggc-muted)] max-w-sm">
          Six distinct builds, each engineered for a different life. Configure, add to your
          selection, and check out securely.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {products.map((p, i) => (
          <FadeUp key={p.id} delay={(i % 3) * 0.08}>
            <div
              className="group border rounded-sm overflow-hidden bg-[var(--ggc-surface)] h-full flex flex-col transition-colors hover:border-[var(--ggc-gold)]"
              style={{ borderColor: "var(--ggc-border)" }}
              data-testid={`product-card-${p.id}`}
            >
              <Link to={`/carts/${p.id}`} className="block relative overflow-hidden aspect-[4/3]">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {p.featured && (
                  <span className="absolute top-4 left-4 bg-gold text-black font-label uppercase tracking-[0.15em] text-[10px] px-3 py-1 rounded-sm">
                    Signature
                  </span>
                )}
              </Link>
              <div className="p-6 flex flex-col flex-1">
                <p className="font-label uppercase tracking-[0.2em] text-[10px] text-[var(--ggc-muted)]">{p.tagline}</p>
                <Link to={`/carts/${p.id}`}>
                  <h3 className="font-serif-ed text-2xl mt-1 group-hover:text-gold transition-colors">{p.name}</h3>
                </Link>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-[var(--ggc-muted)] font-body text-xs">
                  <span>{p.specs.Seating}</span>
                  <span>•</span>
                  <span>{p.specs["Top Speed"]}</span>
                  <span>•</span>
                  <span>{p.specs.Range}</span>
                </div>
                <div className="mt-auto pt-6 flex items-center justify-between">
                  <span className="font-serif-ed text-2xl text-gold">{fmtPrice(p.price)}</span>
                  <button
                    onClick={() => { addItem(p); toast.success(`${p.name} added`); }}
                    className="border font-label uppercase tracking-[0.15em] text-xs px-4 py-2 rounded-sm hover:bg-gold hover:text-black hover:border-transparent transition-colors"
                    style={{ borderColor: "var(--ggc-border)" }}
                    data-testid={`add-to-cart-${p.id}`}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
};

/* ---------------- MANIFESTO ---------------- */
const Manifesto = () => {
  const chapters = [
    { n: "01", t: "Built Without Compromise", b: "Every cart in our collection is sourced and inspected to a standard we would accept for ourselves. No shortcuts, no filler — only machines worth owning." },
    { n: "02", t: "A World Within Reach", b: "From coastal fairways to mountain estates, we deliver worldwide. Wherever the road ends, your cart begins." },
    { n: "03", t: "Service That Endures", b: "Ownership is a relationship, not a transaction. Multi-year warranties and dedicated support keep you moving for the long haul." },
  ];
  return (
    <section id="story" className="relative py-24 lg:py-40 overflow-hidden" style={{ background: "var(--ggc-surface)" }} data-testid="story-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <p className="font-label uppercase tracking-[0.4em] text-xs text-gold mb-4">Our Story</p>
            <MaskedLines as="h2" className="font-serif-ed text-5xl md:text-6xl leading-[0.95]" lines={["Why", "We Exist"]} />
            <div className="mt-10 rounded-sm overflow-hidden border" style={{ borderColor: "var(--ggc-border)" }}>
              <img src={LIFESTYLE} alt="Golfer at sunset" className="w-full h-64 object-cover" />
            </div>
          </div>
          <div className="lg:col-span-8 space-y-16 lg:space-y-24 lg:pt-6">
            {chapters.map((c, i) => (
              <FadeUp key={c.n} delay={i * 0.05}>
                <div className="flex gap-5 md:gap-8 items-start">
                  <span aria-hidden="true" className="font-display text-5xl md:text-7xl leading-none text-gold/30 select-none shrink-0">
                    {c.n}
                  </span>
                  <div className="max-w-xl">
                    <h3 className="font-serif-ed text-3xl md:text-4xl">{c.t}</h3>
                    <p className="font-body text-[var(--ggc-muted)] mt-4 leading-relaxed">{c.b}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- TESTIMONIALS ---------------- */
const Testimonials = () => {
  const quotes = [
    { q: "The Executive arrived flawless, delivered to my driveway in Arizona. It feels less like a golf cart and more like a private club on wheels.", a: "Marcus Reid", r: "Scottsdale, AZ" },
    { q: "I compared a dozen sellers. None matched Global Golf Carts on finish, communication, or the sheer ease of buying online.", a: "Danielle Cho", r: "Naples, FL" },
    { q: "Bought the Estate for our ranch. Two years in, it still turns heads and hasn't missed a beat. Worth every dollar.", a: "Travis Boone", r: "Bozeman, MT" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="testimonials-section">
      <p className="font-label uppercase tracking-[0.4em] text-xs text-gold mb-4">Owners</p>
      <MaskedLines as="h2" className="font-serif-ed text-5xl md:text-7xl mb-16" lines={["What They Say"]} />
      <div className="grid md:grid-cols-3 gap-px" style={{ background: "var(--ggc-border)" }}>
        {quotes.map((t, i) => (
          <FadeUp key={i} delay={i * 0.08} className="bg-[var(--ggc-bg)]">
            <div className="p-8 h-full flex flex-col">
              <span className="font-serif-ed text-6xl text-gold/40 leading-none">“</span>
              <p className="font-serif-ed italic text-xl leading-relaxed mt-2 flex-1">{t.q}</p>
              <div className="mt-8">
                <p className="font-label uppercase tracking-[0.2em] text-sm">{t.a}</p>
                <p className="font-body text-[var(--ggc-muted)] text-xs mt-1">{t.r}</p>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
};

/* ---------------- SHIPPING ---------------- */
const Shipping = () => {
  const items = [
    { icon: Truck, t: "Hassle-Free Delivery Worldwide", b: "Enclosed, insured transport across the globe — included on every purchase, no hidden fees." },
    { icon: MapPin, t: "Door-To-Door", b: "We coordinate directly with you for a delivery window that fits your schedule, right to your address." },
    { icon: ShieldCheck, t: "Protected In Transit", b: "Full-coverage insurance and white-glove handling ensure your cart arrives exactly as it left us." },
  ];
  return (
    <section id="shipping" className="py-24 lg:py-32 border-y" style={{ borderColor: "var(--ggc-border)", background: "var(--ggc-surface)" }} data-testid="shipping-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="font-label uppercase tracking-[0.4em] text-xs text-gold mb-4">Delivery</p>
            <MaskedLines as="h2" className="font-serif-ed text-5xl md:text-6xl leading-[0.95]" lines={["Anywhere", "In The World"]} />
            <p className="font-body text-[var(--ggc-muted)] mt-6 max-w-sm">
              Distance is never a barrier to ownership. From order to arrival, we handle the logistics
              so you can focus on the drive.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-1 gap-px" style={{ background: "var(--ggc-border)" }}>
            {items.map((it, i) => (
              <FadeUp key={i} delay={i * 0.06} className="bg-[var(--ggc-surface)]">
                <div className="p-8 flex gap-6 items-start">
                  <it.icon size={28} strokeWidth={1.3} className="text-gold shrink-0 mt-1" />
                  <div>
                    <h3 className="font-serif-ed text-2xl">{it.t}</h3>
                    <p className="font-body text-[var(--ggc-muted)] mt-2">{it.b}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- CONTACT ---------------- */
const Contact = () => (
  <section id="contact" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="contact-section">
    <div className="grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <p className="font-label uppercase tracking-[0.4em] text-xs text-gold mb-4">Get In Touch</p>
        <MaskedLines as="h2" className="font-serif-ed text-5xl md:text-7xl leading-[0.92]" lines={["Speak With", "A Specialist"]} />
        <p className="font-body text-[var(--ggc-muted)] mt-6 max-w-md">
          Questions about a build, financing, or delivery to your state? Our team is one call or
          email away.
        </p>
      </div>
      <div className="lg:col-span-5 space-y-4">
        <a href="tel:+17178258184" className="group flex items-center gap-5 border rounded-sm p-6 hover:border-[var(--ggc-gold)] transition-colors" style={{ borderColor: "var(--ggc-border)" }} data-testid="contact-phone">
          <Phone size={24} strokeWidth={1.3} className="text-gold" />
          <div>
            <p className="font-label uppercase tracking-[0.2em] text-[10px] text-[var(--ggc-muted)]">Call Us</p>
            <p className="font-serif-ed text-2xl group-hover:text-gold transition-colors">(717)-825-8184</p>
          </div>
        </a>
        <a href="mailto:sales@globalgolfcarts.com" className="group flex items-center gap-5 border rounded-sm p-6 hover:border-[var(--ggc-gold)] transition-colors" style={{ borderColor: "var(--ggc-border)" }} data-testid="contact-email">
          <Mail size={24} strokeWidth={1.3} className="text-gold" />
          <div>
            <p className="font-label uppercase tracking-[0.2em] text-[10px] text-[var(--ggc-muted)]">Email Us</p>
            <p className="font-serif-ed text-2xl group-hover:text-gold transition-colors break-all">sales@globalgolfcarts.com</p>
          </div>
        </a>
      </div>
    </div>
  </section>
);

/* ---------------- FOOTER ---------------- */
const Footer = () => (
  <footer className="border-t py-14" style={{ borderColor: "var(--ggc-border)" }} data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row justify-between items-center gap-6">
      <div className="flex items-center gap-3">
        <img src="/ggc-logo.png" alt="Global Golf Carts LLC" className="h-10 w-10 rounded-full object-cover ring-1 ring-[var(--ggc-gold)]/40" />
        <span className="font-label uppercase tracking-[0.24em] text-xs">Global Golf Carts</span>
      </div>
      <p className="font-body text-[var(--ggc-muted)] text-xs">
        © {new Date().getFullYear()} Global Golf Carts. Delivered nationwide.
      </p>
    </div>
  </footer>
);
